/**
 * COLLECTIONS CLEANUP PAGE (Admin Tool)
 *
 * The `collectionName` field on user items has drifted over time — some
 * values are real polish/plate collections ("Reflections", "Fall 2024"),
 * some are user-invented tag schemes ("1-Movies/TV/HarryPotter",
 * "SPOOKY-Witch", "6-Patterns/Nature"), and many are variant strings of
 * the same underlying collection ("Fall '24" vs "Fall 2024" vs "fall
 * 2024 collection").
 *
 * This page lets an admin:
 *   1. Load all distinct collectionName values with per-value item counts
 *   2. Filter/search the list and multi-select
 *   3. Either
 *      a) MERGE all selected values into one canonical collectionName
 *      b) MOVE all selected values into `tags[]` (with optional rename),
 *         clearing collectionName on those items
 *
 * All writes use the client Firestore SDK; matches the pattern used by
 * MergePolishes / MergeBrands. Gated on `isAdmin === true` on the user's
 * profile doc — same gate as those pages.
 *
 * Post-launch, the deeper fix is preventing new variant strings at the
 * source: turn the free-text `collectionName` input on the Add[Type]
 * modals into an autocomplete against `Set(all values ever used for
 * this brand)`. That reduces new drift; this page cleans up whatever
 * still slips through.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getDocs,
  doc,
  getDoc,
  collectionGroup,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, Pencil, Tag, Trash2, Check, X } from 'lucide-react';
import { slugify } from '../utils/polishSlug';

interface BrandBreakdown {
  brand: string;
  count: number;
}

interface CollectionRow {
  name: string;
  count: number;
  /** Which brand(s) this collectionName appears under, sorted by count
   *  descending. Lets the admin visually verify "Reflections is
   *  Mooncat's, not Essie's" before merging. */
  brands: BrandBreakdown[];
}

const BATCH_LIMIT = 450;
/** How many top brands to show inline in the list row. Extras collapse
 *  behind a "+N more" chip so a name used by 40 brands doesn't blow out
 *  the row height. */
const INLINE_BRAND_LIMIT = 3;

export default function CollectionsCleanup() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which row (if any) the admin is currently inline-renaming. Only one
  // row is editable at a time so the UI stays predictable.
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  /** When set, the rename in progress is scoped to just this brand.
   *  Only meaningful on multi-brand rows (where the same collectionName
   *  legitimately exists across brands — e.g. Mooncat's "Surrealism"
   *  vs Orly's "Surrealism"). Absent → rename is global (safe on
   *  single-brand rows, correct on tag-like values that span brands). */
  const [editingBrand, setEditingBrand] = useState<string | null>(null);

  const [canonicalName, setCanonicalName] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [clearCollectionOnMove, setClearCollectionOnMove] = useState(true);

  const [dryRun, setDryRun] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const say = (m: string) => {
    setLog((prev) => [...prev, m]);
    console.log('[CollectionsCleanup]', m);
  };

  const loadCollections = async () => {
    setLoading(true);
    setLog([]);
    setSelected(new Set());
    try {
      const snap = await getDocs(collectionGroup(db, 'items'));
      // Two-level counts: collectionName → { total, brand → count }.
      // Merges brand strings by lowercased key so casing drift on
      // `brand` itself doesn't fragment the breakdown (a common case —
      // "Essie" and "essie" show as one line).
      const byName = new Map<
        string,
        { total: number; brands: Map<string, { display: string; count: number }> }
      >();
      let considered = 0;
      let skippedNonUser = 0;
      let skippedDeleted = 0;
      let skippedNoCollection = 0;
      for (const d of snap.docs) {
        considered++;
        if (d.ref.parent.parent?.parent?.id !== 'users') {
          skippedNonUser++;
          continue;
        }
        const data = d.data() as Record<string, unknown>;
        if (data.isDeleted === true) {
          skippedDeleted++;
          continue;
        }
        const name =
          typeof data.collectionName === 'string'
            ? data.collectionName.trim()
            : '';
        if (!name) {
          skippedNoCollection++;
          continue;
        }
        const brand =
          typeof data.brand === 'string' && data.brand.trim()
            ? data.brand.trim()
            : '(no brand)';
        let entry = byName.get(name);
        if (!entry) {
          entry = { total: 0, brands: new Map() };
          byName.set(name, entry);
        }
        entry.total += 1;
        const brandKey = brand.toLowerCase();
        const brandEntry = entry.brands.get(brandKey);
        if (brandEntry) {
          brandEntry.count += 1;
        } else {
          entry.brands.set(brandKey, { display: brand, count: 1 });
        }
      }
      const items: CollectionRow[] = Array.from(byName.entries())
        .map(([name, entry]) => ({
          name,
          count: entry.total,
          brands: Array.from(entry.brands.values())
            .map((b) => ({ brand: b.display, count: b.count }))
            .sort((a, b) => b.count - a.count || a.brand.localeCompare(b.brand)),
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      setRows(items);
      say(
        `Loaded ${items.length} distinct collectionName values across ${considered} items (${skippedNonUser} non-user, ${skippedDeleted} deleted, ${skippedNoCollection} without collectionName skipped).`
      );
    } catch (err) {
      say(`Load failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const bf = brandFilter.trim().toLowerCase();
    if (!f && !bf) return rows;
    return rows.filter((r) => {
      if (f && !r.name.toLowerCase().includes(f)) return false;
      // Brand filter matches if ANY brand carrying this collectionName
      // includes the string. Substring match on the display form so a
      // brand-filter of "loaded" catches "Loaded Lacquer".
      if (bf && !r.brands.some((b) => b.brand.toLowerCase().includes(bf)))
        return false;
      return true;
    });
  }, [rows, filter, brandFilter]);

  const totalSelected = selected.size;
  const totalSelectedItems = useMemo(() => {
    let n = 0;
    for (const r of rows) if (selected.has(r.name)) n += r.count;
    return n;
  }, [rows, selected]);

  const toggleSelect = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of filteredRows) next.add(r.name);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  /** Walk user items whose collectionName is in `sourceNames` and apply
   *  `patch(entry)` to each. Batched. Returns a summary.
   *
   *  When `brandLower` is provided, only items whose `brandLower` field
   *  matches are affected — critical when the same collectionName legit
   *  exists across multiple brands (e.g. Mooncat's "Surrealism" vs
   *  Orly's "Surrealism"). Absent brand → global rewrite as before. */
  const applyToMatches = async (
    sourceNames: Set<string>,
    patchFor: (
      current: Record<string, unknown>
    ) => Record<string, unknown> | null,
    brandLower?: string
  ): Promise<{ scanned: number; matched: number; written: number }> => {
    const snap = await getDocs(collectionGroup(db, 'items'));
    let scanned = 0;
    let matched = 0;
    let written = 0;
    let batch = writeBatch(db);
    let ops = 0;
    const sourceLower = new Set(
      Array.from(sourceNames).map((n) => n.toLowerCase())
    );

    for (const d of snap.docs) {
      scanned++;
      if (d.ref.parent.parent?.parent?.id !== 'users') continue;
      const data = d.data() as Record<string, unknown>;
      if (data.isDeleted === true) continue;
      const name =
        typeof data.collectionName === 'string'
          ? data.collectionName.trim()
          : '';
      if (!name) continue;
      if (!sourceLower.has(name.toLowerCase())) continue;

      // Brand scoping — skip items whose brand doesn't match. Accepts
      // EITHER brandLower or brandSlug match so brands with punctuation
      // (e.g. "Polish ALL the Things!") or legacy items with only one
      // field populated still get caught.
      if (brandLower) {
        const itemBrandLower =
          typeof data.brandLower === 'string'
            ? data.brandLower
            : typeof data.brand === 'string'
            ? data.brand.trim().toLowerCase()
            : '';
        const itemBrandSlug =
          typeof data.brandSlug === 'string'
            ? data.brandSlug
            : typeof data.brand === 'string'
            ? slugify(data.brand)
            : '';
        const targetSlug = slugify(brandLower);
        if (itemBrandLower !== brandLower && (!targetSlug || itemBrandSlug !== targetSlug)) continue;
      }

      matched++;
      const patch = patchFor(data);
      if (!patch) continue;
      if (!dryRun) batch.update(d.ref, patch);
      written++;
      ops++;
      if (ops >= BATCH_LIMIT) {
        if (!dryRun) await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0 && !dryRun) await batch.commit();
    return { scanned, matched, written };
  };

  const runMerge = async () => {
    if (totalSelected === 0) {
      say('Nothing selected.');
      return;
    }
    const canonical = canonicalName.trim();
    if (!canonical) {
      say('Enter a canonical collection name first.');
      return;
    }
    setIsRunning(true);
    // Scope by the brand filter if set — matches the per-row semantics
    // (multi-brand rows only act on one brand at a time). Filter is
    // treated as an EXACT match on `brandLower` here, not a substring —
    // otherwise "essie" would collateral-damage "Essie", "Essie Nail
    // Lacquer", and "New Essie" all at once. Substring is fine for the
    // display filter above, not for actions.
    const brandLowerScope = brandFilter.trim().toLowerCase() || undefined;
    say(
      `${dryRun ? '[DRY-RUN] ' : ''}Merge: rewriting collectionName on ${totalSelected} selected value(s) → "${canonical}"${brandLowerScope ? ` (brand exactly "${brandFilter}")` : ''}…`
    );
    try {
      const sourceNames = new Set(selected);
      const summary = await applyToMatches(
        sourceNames,
        (data) => {
          if (data.collectionName === canonical) return null;
          return { collectionName: canonical };
        },
        brandLowerScope
      );
      say(
        `${dryRun ? '(dry-run) ' : ''}Merge complete. Scanned ${summary.scanned}, matched ${summary.matched}, ${dryRun ? 'would-write' : 'wrote'} ${summary.written}.`
      );
      if (!dryRun) {
        say('Refreshing list…');
        await loadCollections();
      }
    } catch (err) {
      say(`Merge failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Inline rename a single collectionName to a new value. Used for the
   *  common "just fix this one typo/casing" flow — no checkbox dance,
   *  no canonical field to fill out. Same underlying logic as the bulk
   *  merge but scoped to one source value. Skips the dry-run gate on
   *  purpose: single-row rename is small enough that pressing the check
   *  button means the admin has already committed to the change. */
  const runRenameOne = async (
    fromName: string,
    toName: string,
    brand?: string
  ) => {
    const trimmed = toName.trim();
    if (!trimmed) {
      say('New name cannot be empty. Use the trash icon to clear collectionName instead.');
      return;
    }
    if (trimmed === fromName) {
      setEditingName(null);
      return;
    }
    setIsRunning(true);
    const brandLower = brand?.trim().toLowerCase();
    say(
      brand
        ? `Renaming "${fromName}" → "${trimmed}" (brand: ${brand} only)…`
        : `Renaming "${fromName}" → "${trimmed}"…`
    );
    try {
      const summary = await applyToMatchesRow(
        new Set([fromName]),
        (data) => (data.collectionName === trimmed ? null : { collectionName: trimmed }),
        false,
        brandLower
      );
      say(
        `Renamed on ${summary.written} item${summary.written === 1 ? '' : 's'}.`
      );
      setEditingName(null);
      await loadCollections();
    } catch (err) {
      say(`Rename failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Move a single collectionName straight into tags (reusing the value
   *  as the tag) and clear the collectionName. One-click shortcut for
   *  the "this was a tag masquerading as a collection" case. */
  const runMoveOneToTag = async (fromName: string, brand?: string) => {
    if (
      !confirm(
        brand
          ? `Move "${fromName}" into item tags and clear collectionName on ${brand}'s items only?`
          : `Move "${fromName}" into item tags and clear collectionName on all matching items?`
      )
    )
      return;
    setIsRunning(true);
    const brandLower = brand?.trim().toLowerCase();
    say(
      brand
        ? `Moving "${fromName}" to tags (brand: ${brand} only)…`
        : `Moving "${fromName}" to tags…`
    );
    try {
      const summary = await applyToMatchesRow(
        new Set([fromName]),
        (data) => {
          const collectionName = String(data.collectionName ?? '').trim();
          if (!collectionName) return null;
          const existing = Array.isArray(data.tags)
            ? (data.tags as string[]).filter((t) => typeof t === 'string')
            : [];
          const existingLower = new Set(existing.map((t) => t.toLowerCase()));
          const already = existingLower.has(collectionName.toLowerCase());
          const patch: Record<string, unknown> = {
            collectionName: deleteField(),
          };
          if (!already) patch.tags = [...existing, collectionName];
          return patch;
        },
        false,
        brandLower
      );
      say(
        `Moved to tags on ${summary.written} item${summary.written === 1 ? '' : 's'}.`
      );
      await loadCollections();
    } catch (err) {
      say(`Move failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Clear collectionName entirely on every item using this value. For
   *  the "this doesn't belong anywhere — just delete it" case. */
  const runClearOne = async (fromName: string, brand?: string) => {
    if (
      !confirm(
        brand
          ? `Clear collectionName on every ${brand} item currently tagged "${fromName}"? This does NOT delete the items themselves — only removes the collectionName field.`
          : `Clear collectionName on every item currently tagged "${fromName}"? This does NOT delete the items themselves — only removes the collectionName field.`
      )
    )
      return;
    setIsRunning(true);
    const brandLower = brand?.trim().toLowerCase();
    say(
      brand
        ? `Clearing collectionName "${fromName}" (brand: ${brand} only)…`
        : `Clearing collectionName "${fromName}"…`
    );
    try {
      const summary = await applyToMatchesRow(
        new Set([fromName]),
        () => ({ collectionName: deleteField() }),
        false,
        brandLower
      );
      say(
        `Cleared on ${summary.written} item${summary.written === 1 ? '' : 's'}.`
      );
      await loadCollections();
    } catch (err) {
      say(`Clear failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  /** Row-scoped variant of applyToMatches that always writes (ignores
   *  the page-level dryRun toggle). Used by the single-row action
   *  handlers above where dry-run doesn't add value.
   *
   *  Same `brandLower` scoping as applyToMatches — the per-row actions
   *  ALWAYS pass a brand for multi-brand rows so a rename to Mooncat's
   *  "Surrealism" doesn't collateral-damage Orly's "Surrealism". */
  const applyToMatchesRow = async (
    sourceNames: Set<string>,
    patchFor: (
      current: Record<string, unknown>
    ) => Record<string, unknown> | null,
    _dryRunOverride: boolean,
    brandLower?: string
  ) => {
    const snap = await getDocs(collectionGroup(db, 'items'));
    const sourceLower = new Set(
      Array.from(sourceNames).map((n) => n.toLowerCase())
    );
    let scanned = 0;
    let matched = 0;
    let written = 0;
    let batch = writeBatch(db);
    let ops = 0;
    for (const d of snap.docs) {
      scanned++;
      if (d.ref.parent.parent?.parent?.id !== 'users') continue;
      const data = d.data() as Record<string, unknown>;
      if (data.isDeleted === true) continue;
      const name =
        typeof data.collectionName === 'string'
          ? data.collectionName.trim()
          : '';
      if (!name) continue;
      if (!sourceLower.has(name.toLowerCase())) continue;
      if (brandLower) {
        const itemBrandLower =
          typeof data.brandLower === 'string'
            ? data.brandLower
            : typeof data.brand === 'string'
            ? data.brand.trim().toLowerCase()
            : '';
        const itemBrandSlug =
          typeof data.brandSlug === 'string'
            ? data.brandSlug
            : typeof data.brand === 'string'
            ? slugify(data.brand)
            : '';
        const targetSlug = slugify(brandLower);
        if (itemBrandLower !== brandLower && (!targetSlug || itemBrandSlug !== targetSlug)) continue;
      }
      matched++;
      const patch = patchFor(data);
      if (!patch) continue;
      batch.update(d.ref, patch);
      written++;
      ops++;
      if (ops >= BATCH_LIMIT) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0) await batch.commit();
    return { scanned, matched, written };
  };

  const runMoveToTags = async () => {
    if (totalSelected === 0) {
      say('Nothing selected.');
      return;
    }
    const explicitTags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    setIsRunning(true);
    const targetTagsSummary =
      explicitTags.length > 0
        ? `→ tags: [${explicitTags.join(', ')}]`
        : '(each item keeps its own collectionName as its tag)';
    say(
      `${dryRun ? '[DRY-RUN] ' : ''}Move: converting ${totalSelected} collection value(s) to tags ${targetTagsSummary}${clearCollectionOnMove ? ' + clearing collectionName' : ''}…`
    );
    try {
      const sourceNames = new Set(selected);
      let tagsAddedTotal = 0;
      const summary = await applyToMatches(sourceNames, (data) => {
        const collectionName = String(data.collectionName ?? '').trim();
        const tagsToAdd =
          explicitTags.length > 0 ? explicitTags : [collectionName];
        const existing = Array.isArray(data.tags)
          ? (data.tags as string[]).filter((t) => typeof t === 'string')
          : [];
        const existingLower = new Set(existing.map((t) => t.toLowerCase()));
        const newTags = tagsToAdd.filter(
          (t) => !existingLower.has(t.toLowerCase())
        );
        const willClear = clearCollectionOnMove && !!collectionName;
        if (newTags.length === 0 && !willClear) return null;
        const patch: Record<string, unknown> = {};
        if (newTags.length > 0) {
          patch.tags = [...existing, ...newTags];
          tagsAddedTotal += newTags.length;
        }
        if (willClear) patch.collectionName = deleteField();
        return patch;
      });
      say(
        `${dryRun ? '(dry-run) ' : ''}Move complete. Scanned ${summary.scanned}, matched ${summary.matched}, ${dryRun ? 'would-write' : 'wrote'} ${summary.written}. Tags ${dryRun ? 'to add' : 'added'}: ${tagsAddedTotal}.`
      );
      if (!dryRun) {
        say('Refreshing list…');
        await loadCollections();
      }
    } catch (err) {
      say(`Move failed: ${(err as Error).message}`);
    } finally {
      setIsRunning(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Checking access…
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        Please sign in to use this admin tool.
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-glass-secondary">
        This page is admin-only.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <h1 className="text-2xl font-bold text-glass mb-2">
        Collections Cleanup
      </h1>
      <p className="text-glass-secondary text-sm mb-3">
        Fix, consolidate, or reclassify the <code>collectionName</code> field
        on user items. Load the list, filter to find what you're looking for,
        then use the per-row actions or the bulk actions at the bottom.
      </p>
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-3 mb-6 text-glass-secondary text-xs space-y-1">
        <div>
          <span className="inline-flex items-center gap-1 mr-1 text-glass">
            <Pencil className="w-3 h-3" /> Rename
          </span>{' '}
          — fix a typo or standardize casing ("Cristmas" → "Christmas",
          "essie fall '24" → "Fall 2024") on one row.
        </div>
        <div>
          <span className="inline-flex items-center gap-1 mr-1 text-glass">
            <Tag className="w-3 h-3" /> Move to tag
          </span>{' '}
          — for values that were really tags all along
          ("1-Movies/TV/HarryPotter", "SPOOKY-Witch"): the value becomes a
          tag on each matching item, and <code>collectionName</code> is
          cleared.
        </div>
        <div>
          <span className="inline-flex items-center gap-1 mr-1 text-glass">
            <Trash2 className="w-3 h-3" /> Clear
          </span>{' '}
          — remove the collectionName field entirely on all matching items
          (the items themselves stay).
        </div>
        <div>
          <span className="text-glass">Checkboxes + bulk actions</span> —
          use when you need to merge multiple variants into one canonical
          name (e.g. select "Halloween", "Halloween ()", "Halloween
          Collection" all at once, then Merge). Green brand chip = single
          brand (safe). Slate chips = row spans multiple brands — in that
          case the per-row actions collapse into per-brand action icons
          on each chip, so you can rename Mooncat's "Surrealism"
          separately from Orly's "Surrealism". The bulk merge is
          brand-scoped by the brand-filter input below when set.
        </div>
      </div>

      {/* Load */}
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={loadCollections}
            disabled={loading || isRunning}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : rows.length > 0 ? 'Reload' : 'Load collection names'}
          </button>
          <div className="text-glass-secondary text-sm">
            {rows.length > 0 && (
              <>
                {rows.length} distinct names · {totalSelected} selected (
                {totalSelectedItems} items)
              </>
            )}
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          {/* Filter + bulk-select controls */}
          <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4 mb-4">
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-glass-muted mb-1">
                  Filter by collection name
                </label>
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="e.g. Halloween, fall 2024, Movies/TV"
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
                />
              </div>
              <div>
                <label className="block text-xs text-glass-muted mb-1">
                  Filter by brand
                </label>
                <input
                  type="text"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  placeholder="e.g. Mooncat, Essie, Emily de Molly"
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
                />
              </div>
            </div>
            <div className="flex gap-2 text-sm">
              <button
                onClick={selectAllFiltered}
                disabled={filteredRows.length === 0}
                className="px-3 py-1.5 bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50"
              >
                Select all filtered ({filteredRows.length})
              </button>
              <button
                onClick={clearSelection}
                disabled={selected.size === 0}
                className="px-3 py-1.5 bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50"
              >
                Clear selection
              </button>
            </div>
          </div>

          {/* List */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg mb-4 max-h-96 overflow-y-auto">
            {filteredRows.length === 0 ? (
              <div className="p-4 text-glass-muted text-sm">
                No collection names match this filter.
              </div>
            ) : (
              <ul>
                {filteredRows.map((r) => {
                  const inlineBrands = r.brands.slice(0, INLINE_BRAND_LIMIT);
                  const extra = r.brands.length - inlineBrands.length;
                  const allBrandsTitle = r.brands
                    .map((b) => `${b.brand} (${b.count})`)
                    .join(', ');
                  const isEditing = editingName === r.name;
                  const isMultiBrand = r.brands.length > 1;
                  return (
                    <li
                      key={r.name}
                      className="flex items-start gap-3 px-4 py-2 border-b border-slate-800 hover:bg-slate-800/50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(r.name)}
                        onChange={() => toggleSelect(r.name)}
                        className="rounded mt-1"
                        disabled={isEditing}
                        title="Check to include in bulk actions below"
                      />
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  runRenameOne(r.name, editingValue, editingBrand ?? undefined);
                                if (e.key === 'Escape') {
                                  setEditingName(null);
                                  setEditingBrand(null);
                                }
                              }}
                              autoFocus
                              className="flex-1 bg-slate-900 border border-purple-500 rounded px-2 py-1 text-glass text-sm"
                            />
                            {editingBrand && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200"
                                title={`Rename scoped to items whose brand is ${editingBrand}. Other brands are untouched.`}
                              >
                                {editingBrand}-only
                              </span>
                            )}
                            <button
                              onClick={() => runRenameOne(r.name, editingValue, editingBrand ?? undefined)}
                              disabled={isRunning}
                              title="Save rename"
                              className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingName(null);
                                setEditingBrand(null);
                              }}
                              disabled={isRunning}
                              title="Cancel"
                              className="p-1 rounded bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div
                            className="text-glass text-sm truncate"
                            title={r.name}
                          >
                            {r.name}
                          </div>
                        )}
                        {/* Brand breakdown. On multi-brand rows each
                            chip becomes an inline action target — pencil
                            / tag / trash operate on THAT brand only, so
                            renaming Mooncat's "Surrealism" doesn't
                            touch Orly's "Surrealism". On single-brand
                            rows the chip is display-only and the global
                            action buttons on the right handle it. */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {inlineBrands.map((b) => (
                            <span
                              key={b.brand}
                              className={
                                r.brands.length === 1
                                  ? 'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300'
                                  : 'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-glass-secondary'
                              }
                              title={`${b.brand}: ${b.count} item${b.count === 1 ? '' : 's'}`}
                            >
                              {b.brand}
                              <span className="opacity-70">· {b.count}</span>
                              {isMultiBrand && !isEditing && (
                                <span className="inline-flex items-center gap-0.5 ml-1 border-l border-slate-500 pl-1">
                                  <button
                                    onClick={() => {
                                      setEditingName(r.name);
                                      setEditingValue(r.name);
                                      setEditingBrand(b.brand);
                                    }}
                                    disabled={isRunning}
                                    title={`Rename "${r.name}" for ${b.brand} only`}
                                    className="hover:text-glass disabled:opacity-30"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => runMoveOneToTag(r.name, b.brand)}
                                    disabled={isRunning}
                                    title={`Move "${r.name}" to tags on ${b.brand}'s items only`}
                                    className="hover:text-glass disabled:opacity-30"
                                  >
                                    <Tag className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => runClearOne(r.name, b.brand)}
                                    disabled={isRunning}
                                    title={`Clear "${r.name}" on ${b.brand}'s items only`}
                                    className="hover:text-red-400 disabled:opacity-30"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </span>
                              )}
                            </span>
                          ))}
                          {extra > 0 && (
                            <span
                              className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-glass-muted"
                              title={allBrandsTitle}
                            >
                              +{extra} more
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-glass-muted text-xs whitespace-nowrap mt-1 mr-2">
                        {r.count} {r.count === 1 ? 'item' : 'items'}
                      </div>
                      {/* Per-row actions — only shown on SINGLE-brand
                          rows. Multi-brand rows require the admin to
                          pick a brand via the per-chip actions above,
                          which prevents accidental collateral damage
                          across brands sharing the same string. */}
                      {!isEditing && !isMultiBrand && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingName(r.name);
                              setEditingValue(r.name);
                              setEditingBrand(r.brands[0]?.brand ?? null);
                            }}
                            disabled={isRunning}
                            title="Rename (fix typo / casing / formatting)"
                            className="p-1.5 rounded text-glass-muted hover:bg-slate-700 hover:text-glass disabled:opacity-30"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => runMoveOneToTag(r.name, r.brands[0]?.brand)}
                            disabled={isRunning}
                            title="Move to tags (converts collectionName into a tag on each item)"
                            className="p-1.5 rounded text-glass-muted hover:bg-slate-700 hover:text-glass disabled:opacity-30"
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => runClearOne(r.name, r.brands[0]?.brand)}
                            disabled={isRunning}
                            title="Clear collectionName on all matching items (items themselves are kept)"
                            className="p-1.5 rounded text-glass-muted hover:bg-red-500/20 hover:text-red-400 disabled:opacity-30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Dry-run toggle applies to both actions */}
          <label className="flex items-center gap-2 mb-4 text-glass text-sm">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry-run (preview counts without writing)
          </label>

          {/* Actions */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Merge */}
            <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4">
              <h2 className="text-glass font-medium mb-2">
                Merge into canonical
              </h2>
              <p className="text-glass-muted text-xs mb-3">
                Rewrites every selected value's items to the canonical
                collectionName. Use when the selected values are variants of
                the same underlying collection.
              </p>
              <label className="block text-xs text-glass-muted mb-1">
                Canonical collectionName
              </label>
              <input
                type="text"
                value={canonicalName}
                onChange={(e) => setCanonicalName(e.target.value)}
                placeholder="e.g. Reflections"
                disabled={isRunning}
                className="w-full mb-3 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
              />
              <button
                onClick={runMerge}
                disabled={isRunning || totalSelected === 0 || !canonicalName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {isRunning ? 'Working…' : dryRun ? 'Preview merge' : 'Merge'}
              </button>
            </div>

            {/* Move to tags */}
            <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-4">
              <h2 className="text-glass font-medium mb-2">Move to tags</h2>
              <p className="text-glass-muted text-xs mb-3">
                Adds the tag(s) below to every selected item and (optionally)
                clears its <code>collectionName</code>. Leave the tag field
                blank to reuse each item's own collectionName as its tag.
              </p>
              <label className="block text-xs text-glass-muted mb-1">
                Target tag(s) — comma-separated, or blank to reuse each name
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g. Halloween, Spooky"
                disabled={isRunning}
                className="w-full mb-2 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
              />
              <label className="flex items-center gap-2 text-glass-muted text-xs mb-3">
                <input
                  type="checkbox"
                  checked={clearCollectionOnMove}
                  onChange={(e) => setClearCollectionOnMove(e.target.checked)}
                />
                Clear collectionName after tagging
              </label>
              <button
                onClick={runMoveToTags}
                disabled={isRunning || totalSelected === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {isRunning ? 'Working…' : dryRun ? 'Preview move' : 'Move to tags'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Log */}
      {log.length > 0 && (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h3 className="text-glass-secondary text-sm mb-2">Log</h3>
          <pre className="text-xs text-glass-muted whitespace-pre-wrap">
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}
